import type { useAuth } from 'react-oidc-context'
// Debug: Force new compilation

import type { GetPerksRequest, WhoAmIPermissions } from '../types'

const TS_PERKS_API = import.meta.env.VITE_API_URL
const API_BASE_PATH = VITE_API_BASE_PATH || ''

// Extended interface for API compatibility
interface ExtendedGetPerksRequest extends GetPerksRequest {
  pageSize?: number
}

type AuthContext = ReturnType<typeof useAuth>

// HTTP Headers interface - compatible with fetch HeadersInit
type HttpHeaders = Record<string, string>

// Essential exports without problematic imports
export const generateKey = (prefix = 'key') => `${prefix}-${Date.now()}`
export const useAnalytics = () => ({
  sendEvent: (_event: string, _value?: unknown) => {
    // Analytics event would be sent here
  },
})

// Utility functions
export const utils = {
  thousandsValueFormat: (value: number) => value?.toLocaleString('en-US'),
  absoluteUrl: (url: string) =>
    url?.startsWith('http') ? url : `https://${url}`,
  formatDate: (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  },
  encodeString: (str: string) => encodeURIComponent(str),
  removeNotViewableCompaniesSearch: (items: unknown[]) => items,
}
// API Client that connects to backend at port 4000
export class AppAPIClient {
  private baseURL =
    typeof window !== 'undefined'
      ? `${TS_PERKS_API}${API_BASE_PATH}`
      : 'http://api:4000'

  auth: AuthContext | undefined

  constructor(auth?: AuthContext) {
    this.auth = auth
  }

  // Mock isClosed method to prevent errors
  isClosed() {
    return false
  }

  // Mock close method to prevent errors
  close() {
    // API connection closed
  }

  async get(url: string) {
    try {
      const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`

      const headers: HttpHeaders = {
        'Content-Type': 'application/json',
      }

      // Add auth headers when user is authenticated
      if (this.auth?.user?.access_token) {
        headers.Authorization = `Bearer ${this.auth.user.access_token}`
      }

      const response = await fetch(fullUrl, {
        headers,
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      console.error('API GET error:', url, error)
      throw error
    }
  }

  async post(url: string, data: unknown) {
    try {
      const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`

      const headers: HttpHeaders = {
        'Content-Type': 'application/json',
      }

      // Add auth headers when user is authenticated
      if (this.auth?.user?.access_token) {
        headers.Authorization = `Bearer ${this.auth.user.access_token}`
      }

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      console.error('API POST error:', url, data, error)
      throw error
    }
  }

  // Mock the perks API methods that the app expects
  perks = {
    get: async (id: string) => {
      // Mock response structure similar to what PerkDetail expects
      return {
        data: {
          _source: {
            company_name: 'Sample Company',
            desc_oneline: 'Sample perk description',
            company_desc_html: '<p>Company description</p>',
            desc_html: '<p>Perk description</p>',
            redemption_html: '<p>Redemption instructions</p>',
            requirements_html: '<p>Requirements</p>',
            value: 1000,
            value_currency: 'USD',
            categories: ['Technology'],
            locations: ['Global'],
            expires_at: '2024-12-31T00:00:00Z',
            created_at: '2024-01-01T00:00:00Z',
            company_url: 'https://example.com',
            logo_url: 'https://via.placeholder.com/176',
            expiration_text: '(in 30 days)',
          },
          _id: id,
        },
      }
    },
    getCategories: async () => {
      return { data: ['Category 1', 'Category 2', 'Category 3'] }
    },
    searchPerks: async (params: ExtendedGetPerksRequest) => {
      return {
        data: [],
        total: 0,
        page: params.page || 1,
        pageSize: params.pageSize || 24,
      }
    },
  }

  // Real v1_perks API that calls backend endpoints
  v1_perks = {
    getCategories: async () => {
      try {
        const response = await this.get('/api/v1/perks/categories')
        return { data: response }
      } catch (error) {
        console.error('Error fetching categories:', error)
        // Fallback to empty array
        return { data: [] }
      }
    },
    getOne: async (id: string) => {
      try {
        const response = await this.get(`/api/v1/perks/${id}`)

        return { data: response }
      } catch (error) {
        console.error('Error fetching perk details:', error)
        throw error
      }
    },
    redeemPerk: async (id: string) => {
      try {
        const response = await this.post(`/api/v1/perks/${id}/redeem`, {})
        return { data: response }
      } catch (error) {
        console.error('Error redeeming perk:', error)
        throw error
      }
    },
    searchPerks: async (params: ExtendedGetPerksRequest) => {
      try {
        // Build query string
        const queryParams = new URLSearchParams()
        if (params.page) queryParams.append('page', params.page.toString())
        if (params.pageSize)
          queryParams.append('size', params.pageSize.toString())
        if (params.categories && params.categories.length > 0) {
          // Ensure categories is always an array
          const categoriesArray = Array.isArray(params.categories)
            ? params.categories
            : [params.categories]
          for (const cat of categoriesArray) {
            queryParams.append('categories', cat)
          }
        }
        if (params.text) queryParams.append('text', params.text)
        if (params.sort) queryParams.append('sort', params.sort)
        if (params.partner) queryParams.append('partner', params.partner)
        if (params.techstarsCompany)
          queryParams.append('techstarsCompany', params.techstarsCompany)

        const url = `/api/v1/search/perks?${queryParams.toString()}`

        const response = await this.get(url)

        // Return raw camelCase data from backend
        return {
          data: response.items || [],
          total: response.total || 0,
          page: response.pages?.page || params.page || 1,
          pageSize: params.pageSize || 24,
          categories: response.categories || [],
        }
      } catch (error) {
        console.error('Error searching perks:', error)
        // Return empty results on error
        return {
          data: [],
          total: 0,
          page: params.page || 1,
          pageSize: params.pageSize || 24,
        }
      }
    },
  }

  v1whoAmI = {
    get: async (): Promise<{ data: WhoAmIPermissions }> => {
      try {
        const response = await this.get('/api/v1/whoami')
        return { data: response as WhoAmIPermissions }
      } catch (error) {
        console.error('Error fetching whoAmI:', error)
        // Fallback with default permissions structure
        return {
          data: {
            id: '',
            email: '',
            name: '',
            canReadPerks: false,
            canWriteMDProfile: false,
            canWriteProgramStaffProfile: false,
            canReadInvestorList: false,
            canWriteProfileEnt: false,
            isCeo: false,
          } as WhoAmIPermissions,
        }
      }
    },
  }

  trackAnalytics = async (
    event: string,
    properties?: Record<string, unknown>,
  ) => {
    try {
      await this.post('/api/v1/analytics/track', {
        event,
        properties,
      })
    } catch (error) {
      console.error('Error tracking analytics:', error)
    }
  }

  // Mock other API endpoints that might be used
  v1References = {
    get: async () => {
      return {
        data: {
          industry_verticals: [],
          business_models: [],
          funding_stages: [],
          locations_of_interest: [],
          investor_types: [],
          investorFirmTypes: [],
          revenue_models: [],
          regions_of_interest: [],
          countries: [],
          program_sessions: [],
          ent_roles: [],
          sustainability_goals: [],
          operating_statuses: [],
        },
      }
    },
  }
}
export const useToastMessage = () => ({
  onCopyToClipboard: (text: string) => navigator.clipboard?.writeText(text),
  renderToastContainer: () => null, // Mock toast container - returns nothing
  canCopyToClipboard: (_text: string) => true, // Simple check - always return true for now
})

// Simple hotjar object - avoid importing the problematic hotjar module
export const hotjar = {
  initialized: () => false,
  identify: () => {},
  event: () => {},
  stateChange: () => {},
}
